/* eslint-disable @typescript-eslint/require-await */

import { useContext, useEffect, useState } from 'react';
import { Logger } from '@feature-hub/logger';
import { ImagesMap, ThemeScroll } from '../FeatureAppTypes';
import { useBreakpoint } from './use-breakpoint';
import { Context } from '../context';
import { isPortrait } from './get-orientation';

export interface UseImagesLoaderProps {
  imagesUrls: string[];
  alt: string;
  theme: ThemeScroll;
}

interface ResolvedImage {
  image: HTMLImageElement;
  index: number;
}

type LoadCallbackType = (resolvedImages: ImagesMap) => void;

/**
 * Load and catche images for sequence in state
 */
export const useImagesLoader = ({ imagesUrls, alt, theme }: UseImagesLoaderProps) => {
  const [imagesLandscape, setImagesLandscape] = useState<ImagesMap>(new Map());
  const [imagesPortrait, setImagesPortrait] = useState<ImagesMap>(new Map());
  const breakpoint = useBreakpoint(theme);
  const { logger } = useContext(Context);

  useEffect(() => {
    // Load media only with valid device width
    if (breakpoint !== 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadMedia(
        imagesUrls,
        (resolvedImages) => {
          const setImages = isPortrait() ? setImagesPortrait : setImagesLandscape;
          const images = isPortrait() ? imagesPortrait : imagesLandscape;
          setImages(new Map([...images, ...resolvedImages]));
        },
        alt,
        logger,
      );
    }
  }, [breakpoint, imagesUrls]);

  return isPortrait() ? imagesPortrait : imagesLandscape;
};

const batchSize = 5;
const loadMedia = async (
  media: string[],
  callback: LoadCallbackType,
  alt: string,
  logger?: Logger,
) => {
  const batchImage: Promise<ResolvedImage>[] = [];

  let startIndex = 0;
  while (startIndex < media.length) {
    const endIndex = Math.min(startIndex + batchSize, media.length);

    // eslint-disable-next-line no-plusplus
    for (let index = startIndex; index < endIndex; index++) {
      batchImage.push(loadImage(media[index], alt).then((image) => ({ image, index })));
    }

    Promise.all(batchImage)
      .then((fetchedImages) => {
        const images: ImagesMap = new Map();
        fetchedImages.forEach((fetchedImage) => {
          if (fetchedImage) {
            const { image, index } = fetchedImage;
            images.set(index, image);
          }
        });

        callback(images);
      })
      .catch(() => logger?.error('URL did not return an image'));

    startIndex += batchSize;
  }
};

const loadImage = (src: string, alt: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      img.alt = alt || '';
      return resolve(img);
    };
    img.onerror = (error) => reject(error);
  });
};
